import { api } from '@cwd/lib/main';

export interface Renewal {
    ServiceSubscriptionId: string;
    CompanyName: string;
    RenewFrom: string | null;
    InvoicedUntil: string | null;
    BillingEnd: string | null;
    Amount: number;
    CurrencyCode: string;
    PeriodFrom: string | null;
    PeriodTo: string | null;
    RenewalPeriod: number;
    Type: string;
    MissingInfo: string;
    ReadyToSend: boolean;
    InvoicingInstructions: string | null;
}

export interface Invoice {
    InvoiceId: string;
    InvoiceNumber: string;
    CompanyName: string;
    RecipientName: string | null;
    RecipientEmail: string | null;
    DueDate: string | null;
    Amount: number;
    Vat: number;
    AmountPaid: number;
    CurrencyCode: string;
    OrderTypes: string[];
    Status: string;
    PaymentMethod: string;
    Created: string | null;
    CreatedByUserName: string | null;
    RemindersSent: number;
    LastReminderSent: string | null;
    PaidDate: string | null;
}

export interface PayableInvoice {
    InvoiceId: string;
    InvoiceNumber: number;
    Amount: number;
    Vat: number;
    AmountPaid: number;
    CompanyName: string;
    DueDate: string | null;
    Status: string;
    CurrencyCode: string;
}

export interface PaymentResult {
    PaymentResult: string;
    NeedsManualIntervention: boolean;
    AdditionalInformation: string | null;
}

export interface MonthlyReport {
    InvoicedPerAccount: Record<string, unknown>[];
    Invoiced: Record<string, unknown>[];
    Unpaid: Record<string, unknown>[];
    PaymentReceived: Record<string, unknown>[];
    StatusUnpaid: Record<string, unknown>[];
    PaidOrders: Record<string, unknown>[];
}

// --- Renewals ---

export async function listRenewals(year: number, month: number): Promise<Renewal[]> {
    return api.get(`/service/ListRenewals?year=${year}&month=${month}`);
}

export async function renew(serviceSubscriptionId: string, startDate: string | null, endDate: string | null): Promise<void> {
    return api.post('/service/Renew', { serviceSubscriptionId, startDate, endDate });
}

export async function renewAll(year: number, month: number): Promise<void> {
    return api.post('/service/RenewAll', { year, month });
}

// --- Invoices ---

function buildInvoiceQs(
    status: number,
    recipientFilter: string,
    from: string,
    to: string,
    invoiceNumber: number | null,
    orderTypeFilter: number | null,
    paymentTypeFilter: number | null,
): string {
    let qs = `?filter=${status}`;
    if (from) qs += `&from=${encodeURIComponent(from)}`;
    if (to) qs += `&to=${encodeURIComponent(to)}`;
    if (invoiceNumber) qs += `&invoiceNumber=${invoiceNumber}`;
    if (recipientFilter) qs += `&recipientFilter=${encodeURIComponent(recipientFilter)}`;
    if (orderTypeFilter) qs += `&orderTypeFilter=${encodeURIComponent(orderTypeFilter)}`;
    if (paymentTypeFilter) qs += `&paymentTypeFilter=${encodeURIComponent(paymentTypeFilter)}`;
    return qs;
}

export async function listInvoices(
    status: number,
    recipientFilter: string,
    from: string,
    to: string,
    invoiceNumber: number | null,
    orderTypeFilter: number | null,
    paymentTypeFilter: number | null,
): Promise<Invoice[]> {
    return api.get(`/service/ListInvoices${buildInvoiceQs(status, recipientFilter, from, to, invoiceNumber, orderTypeFilter, paymentTypeFilter)}`);
}

export async function sendInvoice(invoiceId: string): Promise<void> {
    return api.post(`/service/SendInvoice?invoiceId=${invoiceId}`, {});
}

export async function sendInvoices(
    status: number,
    recipientFilter: string,
    from: string,
    to: string,
    invoiceNumber: number | null,
    orderTypeFilter: number | null,
    paymentTypeFilter: number | null,
): Promise<void> {
    return api.post(`/service/SendInvoices${buildInvoiceQs(status, recipientFilter, from, to, invoiceNumber, orderTypeFilter, paymentTypeFilter)}`, {});
}

export function downloadInvoicePdf(invoiceId: string): void {
    window.open(`/service/InvoicePdf?invoiceId=${invoiceId}`, '_blank');
}

export async function renderInvoice(invoiceNumber: string, reminder: boolean): Promise<string> {
    const mode = reminder ? 'PaperReminder' : 'Paper';
    return api.get(`/service/RenderInvoice?invoiceNo=${invoiceNumber}&invoiceDisplayMode=${mode}`);
}

export async function registerReminder(invoiceNumber: string): Promise<void> {
    return api.post(`/service/RegisterReminder?invoiceNo=${invoiceNumber}`, {});
}

function toFormEncoded(value: unknown): string {
    return `=${encodeURIComponent(value == null ? '' : String(value))}`;
}

export async function cancelInvoice(invoiceId: string, reason: string): Promise<void> {
    return api.post(`/service/CancelInvoice/${invoiceId}`, toFormEncoded(reason), 'application/x-www-form-urlencoded; charset=UTF-8');
}

// --- Register Payment ---

export async function loadInvoiceToPay(invoiceNumber: string): Promise<PayableInvoice> {
    return api.get(`/service/LoadInvoiceToPay?invoicenumber=${invoiceNumber}`);
}

export async function addPayment(invoiceId: string, payDate: string, amount: number, acceptedDiff: number): Promise<PaymentResult> {
    return api.post('/service/AddPayment', { invoiceId, payDate, amount, acceptedDiff });
}

// --- Unresolved ---

export async function resolveInvoice(invoiceId: string): Promise<void> {
    return api.post(`/service/ResolveInvoice?invoiceId=${invoiceId}`, {});
}

// --- Order Editor ---

export interface EditOrderRow {
    RowNumber: number | null;
    Specification: string;
    Amount: number;
    Vat: number;
    BookkeepingAccount: string;
    RefersToMonth: string | null;
}

export interface EditableOrder {
    OrderId: string | null;
    InvoiceNumber: number | null;
    ServiceSubscriptionId: string | null;
    CompanyName: string;
    Attention: string;
    RecipientName: string;
    RecipientAddress: string;
    RecipientEmail: string;
    Specification: string;
    CurrencyCode: string;
    AddVat: boolean;
    DueDate: string | null;
    OrderRows: EditOrderRow[];
    CreditsInvoiceNumber: number | null;
    Status: string;
    StatusString: string;
    AmountPaid: number | null;
    Language: string;
    PaymentTerms: number;
    OrderType: string;
    Availability: string;
    InvoicedUntil: string | null;
}

export function newOrder(): EditableOrder {
    return {
        OrderId: null,
        InvoiceNumber: null,
        ServiceSubscriptionId: null,
        CompanyName: '',
        Attention: '',
        RecipientName: '',
        RecipientAddress: '',
        RecipientEmail: '',
        Specification: '',
        CurrencyCode: 'SEK',
        AddVat: false,
        DueDate: null,
        OrderRows: [],
        CreditsInvoiceNumber: null,
        Status: 'ReadyToSend',
        StatusString: 'ReadyToSend',
        AmountPaid: null,
        Language: 'sv',
        PaymentTerms: 30,
        OrderType: 'Manual',
        Availability: 'ReadyToSend',
        InvoicedUntil: null,
    };
}

export async function loadOrder(orderId: string): Promise<EditableOrder> {
    return api.get(`/service/Order/${orderId}`);
}

export async function saveOrder(order: EditableOrder): Promise<void> {
    return api.post('/service/saveorder', order);
}

export async function getBookkeepingAccounts(): Promise<string[]> {
    return api.get('/service/BookkeepingAccounts');
}

// --- Orders ---

export interface InvoicableOrder {
    CompanyName: string;
    Amount: number;
    OrderType: string;
    CurrencyCode: string;
    OrderId: string;
    User: { UserName: string } | null;
    OrderDate: string | null;
    Status: number;
    StatusString: string;
    InvoiceId: string | null;
    PaidInFull: boolean;
}

export interface InvoiceAllResult {
    InvoicesGenerated: number;
    Failures: string[];
}

export async function listOrders(filter: number, from: string, to: string, recipientFilter: string): Promise<InvoicableOrder[]> {
    let qs = `?filter=${filter}`;
    if (from) qs += `&from=${encodeURIComponent(from)}`;
    if (to) qs += `&to=${encodeURIComponent(to)}`;
    if (recipientFilter) qs += `&recipientFilter=${encodeURIComponent(recipientFilter)}`;
    return api.get(`/service/ListOrders${qs}`);
}

export async function createInvoiceFromOrder(orderId: string): Promise<void> {
    return api.post('/service/createInvoice', orderId);
}

export async function deleteOrder(orderId: string): Promise<void> {
    return api.delete(`/service/Order/${orderId}`);
}

export async function invoiceAllReadyToSend(): Promise<InvoiceAllResult> {
    return api.post('/service/Order/InvoiceAll', {});
}

// --- Accounts Receivable ---

export interface ClientCompanySearchResult {
    ServiceSubscriptionId: string;
    BillingName: string;
    CompanyName: string;
    BillingAddress: string;
    BillingAddress1: string | null;
    BillingAddress2: string | null;
    BillingAttention: string | null;
    BillingCity: string | null;
    BillingZipCode: string | null;
    BillingCountry: string | null;
    BillingEmail: string | null;
    Attention: string | null;
    PaysVat: boolean;
    DefaultCurrencyCode: string | null;
}

export interface AccountsReceivableEntry {
    Type: string;
    SourceId: string;
    InvoiceNumber: number | null;
    Amount: number;
    Vat: number;
    CurrencyCode: string;
    Date: string;
    Status: string;
    BookkeepingCode: string | null;
}

export async function findClientCompany(searchArg: string): Promise<ClientCompanySearchResult[]> {
    return api.get(`/service/FindClientCompany?searchArg=${encodeURIComponent(searchArg)}`);
}

export async function getAccountsReceivable(serviceSubscriptionId: string, from: string, to: string): Promise<AccountsReceivableEntry[]> {
    return api.get(`/service/${serviceSubscriptionId}/AccountsReceivable?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
}

// --- Monthly Report ---

export async function getMonthlyReport(year: number, month: number): Promise<MonthlyReport> {
    return api.get(`/service/monthlyreport?year=${year}&month=${month}`);
}

export function getMonthlyReportExportUrl(year: number, month: number): string {
    return `/service/ExportMonthlyReport?year=${year}&month=${month}`;
}
